<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script module>
  /**
   * @typedef { "small" | "medium" | "large" } MapSize
   */
  /**
   * @typedef { Object } MapToolProps
   * @property { Array<Record<string, any>> } data
   * @property { Array<Record<string, any>> } [tableData]
   * @property { import("$utils/config").ColumnConfig[] } configData
   * @property { import("$lib/types").mapType } [mapType="hex"]
   * @property { string } [accentColor = "#Fdbf11"]
   * @property {import("$utils/states").FeatureOptions} [featureOption="states"]
   * @property { boolean } [showStateSummary = true]
   * @property { string } [dropdownLabel]
   * @property { string } [tablePlaceholderLabel]
   * @property { boolean } [showDropdown = false]
   * @property { boolean } [centerDropdown = false]
   * @property { MapSize } [mapSize = "medium"]
   * @property { string } [toolTitle=""]
   * @property { string } [headerText="View data for [dropdown]"]
   * @property { string } [stateDescriptionTemplate="View data for [state] below."]
   */
</script>

<script>
  import MapComponent from "$lib/components/Map/Map.svelte";
  import DetailHeader from "$lib/components/DetailHeader/DetailHeader.svelte";
  import Table from "$lib/components/Table/Table.svelte";
  import { Block, BasicDropdown, Theme } from "@urbaninstitute/dataviz-components";
  import windowWidth from "$lib/stores/windowWidth.js";
  import { createConfig, setConfig, getTableProps } from "$lib/utils/config.js";
  import { getStatesInfo, getStateName } from "$utils/states.js";
  import InfoBox from "$lib/components/InfoBox/InfoBox.svelte";
  import InfoBoxLayout from "$lib/components/InfoBoxLayout/InfoBoxLayout.svelte";
  import SectionB from "$lib/components/SectionB/SectionB.svelte";
  import SectionC from "$lib/components/SectionC/SectionC.svelte";
  import SectionD from "$lib/components/SectionD/SectionD.svelte";
  import SectionE from "$lib/components/SectionE/SectionE.svelte";
  import { formatTextTemplate } from "$utils/text.js";
  import { ToolData } from "$utils/data.js";

  /** @type {MapToolProps} */
  const {
    data,
    tableData,
    configData,
    mapType = "hex",
    mapSize = "medium",
    showStateSummary = true,
    accentColor = "#fdbf11",
    featureOption = "states",
    dropdownLabel = "",
    tablePlaceholderLabel = "Select a state or district to see data by county",
    showDropdown = true,
    centerDropdown = false,
    toolTitle = "",
    headerText = "View data for [dropdown]",
    stateDescriptionTemplate = "View data for [state] below."
  } = $props();

  // use this effect to load data from the URLs passed as properties
  $effect(() => {
    // fetch primary data and store in $state
    if (!parsedData) {
      parsedData = new ToolData(data, tableData);
    }
    // fetch config data and store in $state
    if (!parsedConfigData) {
      parsedConfigData = createConfig(configData, accentColor);
    }
  });

  /**
   * @param {Record<string, any>[]} allData
   * @param {string} map_id
   * @returns {Record<string, any>[]}
   */
  function getTableData(allData, map_id) {
    return allData.filter((d) => d.map_id === map_id);
  }

  let currentState = $state();
  let currentMetric = $state();
  let currentDate = $state();
  // use effect to update current Metric and Date to defaults if not defined
  $effect(() => {
    if (parsedData && parsedConfigData && dataMetrics.length > 0) {
      const allowedMetrics = dataMetrics.map((d) => d.value);

      if (!currentMetric || !allowedMetrics.includes(currentMetric)) {
        currentMetric = allowedMetrics[0];
      }
      if (parsedData && !currentDate && parsedData.hasTimeSeries) {
        currentDate = parsedData.getLatestDateForColumn(currentMetric);
      }
    }
  });

  /**
   * State to hold primary data
   * @type {ToolData | undefined}
   */
  let parsedData = $state();
  // state to hold configuration data
  let parsedConfigData = $state();
  // is the tool using the primary data for the table?
  let tableUsesMainData = $state(false);

  // set the config data into the context so child components can access it without prop drilling
  setConfig(() => parsedConfigData);

  // parse state summary into boolean
  const shouldShowStateSummary = $derived(showStateSummary === true && !tableUsesMainData);

  // derived state to hold the current table data, which is filtered based on the state selection
  let currentTableData = $derived(
    parsedData && currentState
      ? parsedData.getDetailDataForId(currentState, shouldShowStateSummary)
      : []
  );

  // derived state to hold the current map configuration, which is set based on the metric selection
  let currentMapConfig = $derived(
    currentMetric && parsedConfigData ? parsedConfigData.columns.get(currentMetric) : undefined
  );

  // svelte-ignore state_referenced_locally
  const stateDropdownData = getStatesInfo(featureOption).map(({ fips, state }) => ({
    value: fips,
    label: state
  }));

  const currentStateData = $derived(
    parsedData ? parsedData.getRow(currentState, currentDate) : undefined
  );
  const currentStateVariables = $derived(getStateVariables(currentStateData));

  // Extract colNames from parsedData
  let tableColNames = $derived(parsedData ? parsedData.getDetailColumns() : []);

  let tableProps = $derived(
    parsedConfigData && tableColNames.length > 0
      ? getTableProps(parsedConfigData, tableColNames)
      : { formatters: {}, headers: {} }
  );

  // build dropdown options from colNames where show_on_map is true
  const dataMetrics = $derived(
    parsedData && parsedConfigData
      ? parsedData
          .getPrimaryColumns()
          .filter((k) => {
            const colConfig = parsedConfigData.columns.get(k);
            return colConfig && colConfig.show_on_map !== false;
          })
          .map((k) => ({ value: k, label: parsedConfigData.columns.get(k).column_label }))
          .sort((a, b) => {
            const aOrder = parsedConfigData.columns.get(a.value)?.map_order;
            const bOrder = parsedConfigData.columns.get(b.value)?.map_order;
            if (aOrder == null && bOrder == null) return 0;
            if (aOrder == null) return 1;
            if (bOrder == null) return -1;
            return aOrder - bOrder;
          })
      : []
  );

  /**
   * @param {{fips?: string, map_id?: string} | null | undefined} props
   */
  function handleStateSelect(props) {
    if (!props) {
      currentState = null;
      return;
    }
    const { fips, map_id } = props;
    currentState = fips || map_id || null;
  }

  /**
   * @param {string} metric
   * @param {number} maxWidth
   */
  function getDropdownWidth(maxWidth) {
    let padding = 32 + 16;
    return Math.min(760, maxWidth - padding);
  }
  /**
   * @param {Record<string, any> | undefined} stateData
   * @returns {Record<string, string | null> | undefined}
   */
  function getStateVariables(stateData) {
    if (!parsedConfigData || !stateData) {
      return undefined;
    }
    /** @type {Record<string, string | null>} */
    let result = {
      state: getStateName(stateData.map_id)
    };
    for (let key in stateData) {
      let colConfig = parsedConfigData.columns.get(key);
      if (colConfig?.format) {
        result[key] = colConfig.format(stateData[key]);
      } else {
        result[key] = String(stateData[key]);
      }
    }
    return result;
  }
</script>

<svelte:window bind:innerWidth={$windowWidth} />
<Theme>
  <div class="tool-container">
    {#if toolTitle}
      <Block width="body"><h4 class="tool-title">{toolTitle}</h4></Block>
    {/if}
    <Block width="wide">
      {#if parsedData && parsedConfigData}
        {#if showDropdown}
          <div
            class="tool-header"
            class:center-align={centerDropdown}
            style={`--accent-color: ${accentColor};`}
          >
            {dropdownLabel}
            <BasicDropdown
              data={dataMetrics}
              variant="secondary-yellow"
              inlineLabel="Select a metric to view"
              id="metric-select"
              placeholder={null}
              dropdownWidth={getDropdownWidth($windowWidth)}
              onchange={(e) => {
                // logClickToGA(e.target, "metric-select--" + e.target.value);
              }}
              bind:value={currentMetric}
            />
          </div>
        {/if}
        {#if currentMapConfig}
          <div class="map-wrapper {mapSize}">
            <MapComponent
              data={parsedData.getSnapshot(currentDate)}
              metric={currentMetric}
              onStateSelect={handleStateSelect}
              colorPalette={currentMapConfig.scale_color}
              scaleBreaks={currentMapConfig.scale_breaks}
              scaleType={currentMapConfig.scale_type}
              minMax={currentMapConfig.scale_min_max}
              {featureOption}
              {mapType}
              bind:currentState
            />
          </div>
        {/if}
        <div class="state-details wide">
          <DetailHeader bind:currentState {stateDropdownData} {headerText} />
          <InfoBoxLayout>
            <InfoBox span={2}>
              <SectionB {parsedData} {currentState} />
            </InfoBox>
            <InfoBox span={2}>
              <SectionC {parsedData} {currentState} />
            </InfoBox>
            <InfoBox span={1}>
              <SectionD {parsedData} {currentState} />
            </InfoBox>
            <InfoBox span={1}>
              <SectionE {parsedData} {currentState} />
            </InfoBox>
          </InfoBoxLayout>
        </div>
      {/if}
    </Block>
  </div>
</Theme>

<style>
  h4.tool-title {
    margin-bottom: var(--spacing-12) !important;
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    max-width: 760px;
    margin-inline: auto;
    margin-bottom: var(--spacing-8);
  }
  .tool-header.center-align {
    text-align: center;
  }
  .tool-header :global(.dropdown-parent) {
    display: inline-block;
  }
  .tool-header :global(.dropdown-container .icons svg) {
    display: block;
  }
  .tool-header :global(.dropdown-container select.dropdown-select) {
    background-color: var(--accent-color);
    border: var(--accent-color);
  }
  .tool-header :global(select) {
    font-size: var(--font-size-2xl) !important;
  }
  .tool-header :global(select option) {
    font-size: var(--font-size-normal) !important;
  }
  .state-details {
    margin: var(--spacing-8) auto;
    max-width: var(--size-body-max-width);
  }
  .state-details.wide {
    max-width: var(--size-wide-max-width);
  }
  .map-wrapper {
    margin-inline: auto;
  }
  .map-wrapper.small {
    max-width: 760px;
  }
  .map-wrapper.medium {
    max-width: 960px;
  }
  .map-wrapper.large {
    max-width: 1160px;
  }
  .box-description {
    margin-block-start: 0;
  }
  h4.box-title {
    margin-block-start: 0;
  }
  @media (max-width: 540px) {
    .tool-header :global(select) {
      font-size: var(--font-size-xl) !important;
    }
    .tool-header {
      font-size: var(--font-size-xl) !important;
    }
  }
  .tool-container :global(.tooltip h5) {
    font-size: 16px !important;
    line-height: 24px;
  }
  .tool-container :global(.tooltip .tooltip-text p) {
    line-height: 21px !important;
  }
  .tool-container :global(.tooltip .tooltip-title) {
    margin-bottom: var(--spacing-8);
  }
  .tool-container :global(h5) {
    font-size: 20px;
    line-height: 28px;
  }
  .tool-container :global(.section-title) {
    margin-block-start: 0;
    margin-block-end: var(--spacing-4);
  }
  .tool-container :global(.chart-label) {
    font-size: 16px;
    line-height: 24px;
    font-style: italic;
  }
  .tool-container :global(.section-description) {
    font-size: 16px;
    line-height: 24px;
  }
  @media (min-width: 768px) {
    .tool-container :global(h5) {
      font-size: 24px;
      line-height: 32px;
    }
    .tool-container :global(.tooltip h5) {
      font-size: 18px !important;
      line-height: 24px;
    }
  }
</style>
